import { getCategoryIcon } from '@/components/ui/transcript-chips/types';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { ChevronDown, Lightbulb, PanelRight, X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  EvidencePromptSuggestion,
  EvidencePromptsBarProps,
} from './evidence-prompts-bar.types';
import { StackedSourceAvatars } from './stacked-source-avatars';

/** Animation easing: ease-out-quint */
const EASE_OUT_QUINT = [0.23, 1, 0.32, 1] as const;

function SuggestionItem({
  suggestion,
  onClick,
  onHover,
}: {
  suggestion: EvidencePromptSuggestion;
  onClick: () => void;
  onHover?: (query: string | null) => void;
}) {
  const Icon = getCategoryIcon(suggestion.category);

  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full justify-start gap-2 p-3 text-text-primary"
      onClick={onClick}
      onMouseEnter={() => onHover?.(suggestion.query)}
      onMouseLeave={() => onHover?.(null)}
    >
      <Icon className="size-4 shrink-0 text-text-secondary" />
      <TypographyP2 className="truncate text-text-primary">
        {suggestion.label}
      </TypographyP2>
    </Button>
  );
}

export function EvidencePromptsBar({
  suggestions,
  sourceFavicons,
  expanded,
  onExpandedChange,
  onSuggestionClick,
  onSuggestionHover,
  onDismiss,
  onOpenSidebar,
  className,
}: EvidencePromptsBarProps) {
  const intl = useIntl();
  const hasSuggestions = suggestions.length > 0;
  const suggestionsRegionId = 'evidence-prompts-suggestions';

  const headerContent = (
    <>
      <div className="flex items-center gap-1">
        <Lightbulb className="size-3 shrink-0 text-text-secondary" />
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage({
            defaultMessage: 'Considerations',
            id: '9kpxviV5Jg',
          })}
        </TypographyCaption>
      </div>

      {sourceFavicons.length > 0 && (
        <StackedSourceAvatars sources={sourceFavicons} maxVisible={3} size="md" />
      )}
    </>
  );

  return (
    <div
      data-testid="evidence-prompts-bar"
      className={cn(
        'overflow-hidden rounded-t-lg border-x border-t border-border bg-surface pb-3',
        className
      )}
    >
      {/* Collapsed header row */}
      <div className="flex items-center gap-4 p-2">
        {hasSuggestions ? (
          <button
            type="button"
            className="flex flex-1 cursor-pointer appearance-none items-center justify-between bg-transparent p-0"
            onClick={() => onExpandedChange(!expanded)}
            aria-expanded={expanded}
            aria-controls={suggestionsRegionId}
          >
            {headerContent}
          </button>
        ) : (
          <div className="flex flex-1 items-center justify-between">{headerContent}</div>
        )}

        <div className="flex items-center">
          {expanded ? (
            <>
              {onOpenSidebar && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="xxs"
                        iconOnly
                        onClick={onOpenSidebar}
                        aria-label={intl.formatMessage({
                          defaultMessage: 'Open full chat',
                          id: 'kvP/z+FPb+',
                        })}
                      >
                        <PanelRight className="size-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <FormattedMessage id="kvP/z+FPb+" defaultMessage="Open full chat" />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Button
                variant="ghost"
                size="xxs"
                iconOnly
                onClick={() => onExpandedChange(false)}
                aria-label={intl.formatMessage({
                  defaultMessage: 'Collapse considerations',
                  id: 'q2ArUEq5pp',
                })}
              >
                <ChevronDown className="size-3.5" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="xxs"
              iconOnly
              onClick={onDismiss}
              aria-label={intl.formatMessage({
                defaultMessage: 'Dismiss considerations',
                id: 'XTFKNiOfSr',
              })}
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded suggestions */}
      <AnimatePresence>
        {expanded && hasSuggestions && (
          <m.div
            id={suggestionsRegionId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: EASE_OUT_QUINT },
              opacity: { duration: 0.2, ease: 'easeOut' },
            }}
            className="overflow-hidden"
          >
            <div className="px-1 pb-1">
              {suggestions.slice(0, 3).map((suggestion, index) => (
                <div key={suggestion.id}>
                  {index > 0 && <div className="mx-2 border-t border-border" />}
                  <SuggestionItem
                    suggestion={suggestion}
                    onClick={() => onSuggestionClick(suggestion)}
                    onHover={onSuggestionHover}
                  />
                </div>
              ))}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
